import React, { useState, useRef } from 'react';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';
import { 
    collection, 
    query, 
    where, 
    getDocs,
    getDoc, 
    doc 
} from 'firebase/firestore';
// --- CONSTANTS & HELPERS ---

// Add-on mapping for transformation (from boolean flags to display objects)
// FIX: Removed hardcoded 'price: 10.00' to prevent incorrect display when staff 
// has not provided per-item pricing in the approvedAddOns array. (Per user request)
const ADDON_RATES_MAP = {
    breakfast: { name: 'Breakfast' }, 
    parking: { name: 'Parking' }, 
    airportTransfer: { name: 'Airport Transfer' }, 
    extraBed: { name: 'Extra Bed' }, 
    roomUpgrade: { name: 'Room Upgrade' }, 
    spaAppointment: { name: 'Spa Appointment' }
};

// Helper function to format currency
const formatCurrency = (amount) => amount ? `$${parseFloat(amount).toFixed(2)}` : 'N/A';
// Helper function to safely get display date from Firestore Timestamp
const getDisplayDate = (dateField) => {
    if (!dateField) return 'TBD';
// Handle both Firestore Timestamp object and regular Date string
    const date = typeof dateField.toDate === 'function' ?
dateField.toDate() : new Date(dateField);
    return (date instanceof Date && !isNaN(date)) ? date.toLocaleDateString() : 'TBD';
};
// Robust date utility for calculation
const safeToDate = (timestampOrString) => {
    if (!timestampOrString) return null;
if (typeof timestampOrString === 'object' && timestampOrString.seconds) {
        return timestampOrString.toDate();
}
    const parsedDate = new Date(timestampOrString);
    if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
}
    return null; 
};

// New Helper function to display payment status and price due
const getPaymentStatusDisplay = (guest) => {
    const isFullyPaid = guest.paymentStatus === 'PAID_IN_FULL' ||
(guest.paymentStatus === 'PARTIAL_PAYMENT' && (guest.amountDue === 0 || !guest.amountDue));
    
    // Use the finalized price fields from the staff form
    const finalPrice = guest.adjustedFinalPrice ||
guest.totalPrice || 0;
    const amountPaid = guest.amountPaid || 0;
    const amountDue = finalPrice - amountPaid;
if (isFullyPaid || amountDue <= 0.01) {
        return {
            label: 'Payment Status:',
            value: 'FULLY PRE-PAID',
            color: '#28a745',
            isPrePaid: true
        };
} else if (guest.paymentStatus === 'PARTIAL_PAYMENT' || guest.paymentStatus === 'AWAITING_PAYMENT') {
        return {
            label: 'Amount Due at Check-in:',
            value: formatCurrency(amountDue),
            color: '#dc3545',
            isPrePaid: false
        };
} else {
        return {
            label: 'Payment Status:',
            value: guest.paymentStatus ||
'AWAITING_PAYMENT',
            color: '#ffc107',
            isPrePaid: false
        };
}
};


// --- FINAL INVOICE COMPONENT (Used for Print/PDF) ---
const FinalGuestInvoice = ({ guest }) => { 
    const { t } = useTranslation();
    // Use finalized values from the staff's check-in form
    const roomPrice = guest.roomPrice ||
0;
    // Safely retrieve staff-adjusted tax rates, defaulting if not set
    const taxRate = guest.taxRate >= 0 ?
guest.taxRate : 0.10; // Use 0.10 (10%) as a common default
    // FIX for ReferenceError: touristTax
    const touristTax = guest.touristTax >= 0 ?
guest.touristTax : 0.00; // Default to 0
    const addOnsPrice = guest.addOnsPrice || 0.00;
const finalPrice = guest.adjustedFinalPrice || guest.totalPrice || 0;

    // Calculate details (using arrival/departure for initial booking length)
    const checkInDate = safeToDate(guest.checkInDate || guest.arrivalTime);
const checkOutDate = safeToDate(guest.checkOutDate || guest.departureTime);
    
    let totalDays = 0;
    let subtotal = 0;
    let taxAmount = 0;
let touristTaxTotal = 0;

    if (checkInDate && checkOutDate) {
        // Calculate total days, ensuring it's at least 1 night for a booking
        totalDays = Math.max(1, Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / 86400000));
subtotal = roomPrice * totalDays;
        taxAmount = subtotal * taxRate;
        touristTaxTotal = touristTax * totalDays;
}

    const totalDue = finalPrice;
    
    // Get Add-ons and Preferences for display
    // NOTE: Invoice always uses the finalized, 'approved' fields
    const approvedAddOns = Array.isArray(guest.approvedAddOns) ?
guest.approvedAddOns : [];
    const preferredItems = Array.isArray(guest.preferredItems) ? guest.preferredItems : [];
// Professional Invoice Style
    const invoiceStyle = {
        fontFamily: 'Arial, sans-serif',
        border: '1px solid #ccc',
        padding: '30px',
        maxWidth: '700px',
        margin: '20px auto',
        backgroundColor: '#fff',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    };
const headerStyle = {
        borderBottom: '3px solid #dc3545',
        paddingBottom: '15px',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    };
const detailRowStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '10px 0',
        borderBottom: '1px dashed #eee'
    };
const summaryStyle = {
        borderTop: '3px solid #28a745', // Green for final price
        marginTop: '25px',
        paddingTop: '15px',
        fontWeight: 'bold'
    };
const listItemStyle = {
        fontSize: '0.9em',
        padding: '2px 0',
        marginLeft: '20px',
        listStyleType: 'disc'
    };
return (
        <div style={invoiceStyle} id="final-invoice-print-area">
            <div style={headerStyle}>
                <div>
                    <h2 style={{ margin: 0, color: '#dc3545' }}>{t('checker.final_tax_invoice')}</h2>
                    <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>{t('checker.reference')} **{guest.bookingKey || 
'N/A'}**</p>
                    <p style={{ margin: '2px 0 0 0', fontSize: '0.9em' }}>{t('checker.date')} {new Date().toLocaleDateString()}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <h3 style={{ margin: 0 }}>{guest.hotelName || t('checker.unknown_hotel')}</h3>
      
               <p style={{ margin: '2px 0 0 0', fontSize: '0.8em' }}>{guest.hotelAddress || "N/A"}</p>
                </div>
            </div>

            <h4 style={{ borderBottom: '1px solid #ddd', paddingBottom: '5px', color: '#333' }}>{t('checker.stay_details_title')}</h4>
            <p style={{ margin: '5px 0' }}>**{guest.fullName}** | Email: {guest.email}</p>
  
           <div style={detailRowStyle}>
                <span>{t('checker.checkin_date')}</span>
                <span>{getDisplayDate(guest.checkInDate ||
guest.arrivalTime)}</span>
            </div>
            <div style={detailRowStyle}>
                <span>{t('checker.checkout_date')}:</span>
                <span>{getDisplayDate(guest.checkOutDate || guest.departureTime)}</span>
            </div>
            <div style={detailRowStyle}>
               
 <span>{t('checker.total_nights')}</span>
                <span>{totalDays}</span>
            </div>
            <div style={detailRowStyle}>
                <span>{t('checker.assigned_room')}</span>
                <span style={{ fontWeight: 'bold', color: '#dc3545' }}>{guest.roomNumber ||
'N/A'}</span>
            </div>
            <div style={detailRowStyle}>
                <span>{t('checker.room_type')}</span>
                <span>{guest.roomType ||
'N/A'}</span>
            </div>
            
            {/* Approved Add-ons Section */}
            <h4 style={{ borderBottom: '1px solid #ddd', paddingBottom: '5px', marginTop: '30px', color: '#333' }}>{t('checker.services_title')}</h4>
            <p style={{ margin: '5px 0 0 0', fontWeight: 'bold' }}>{t('checker.approved_addons')}</p>
       
         {approvedAddOns.length > 0 ? (
                <ul style={{ margin: '5px 0 10px 0', padding: 0 }}>
                    {approvedAddOns.map((item, index) => (
                        <li key={`addon-${index}`} style={listItemStyle}>
                 
             {item.name} {item.price ? `(${formatCurrency(item.price)})` : ''}
                        </li>
                    ))}
                </ul>
            ) : (
          
        <p style={{ ...listItemStyle, listStyleType: 'none', marginLeft: 0 }}>{t('checker.none_approved')}</p>
            )}
            
            <p style={{ margin: '5px 0 0 0', fontWeight: 'bold' }}>{t('checker.guest_preferences')}</p>
            {preferredItems.length > 0 ?
(
                <ul style={{ margin: '5px 0 10px 0', padding: 0 }}>
                    {preferredItems.map((item, index) => (
                        <li key={`pref-${index}`} style={listItemStyle}>
                          
    {item}
                        </li>
                    ))}
                </ul>
            ) : (
                <p style={{ ...listItemStyle, listStyleType: 'none', marginLeft: 0 }}>{t('checker.no_preferences')}</p>
            )}


            <h4 style={{ borderBottom: '1px solid #ddd', paddingBottom: '5px', marginTop: '30px', color: '#333' }}>{t('checker.price_breakdown_title')}</h4>

            <div style={detailRowStyle}>
                <span>{t('checker.price_per_night')}</span>
                <span>{formatCurrency(roomPrice)}</span>
          
    </div>
            <div style={detailRowStyle}>
                <span>{t('checker.subtotal_nights', { count: totalDays })}</span>
                <span>{formatCurrency(subtotal)}</span>
            </div>
            <div style={detailRowStyle}>
                <span>{t('checker.tourist_tax', { price: formatCurrency(touristTax) })}</span>
        
                 <span>{formatCurrency(touristTaxTotal)}</span>
            </div>
            <div style={detailRowStyle}>
                <span>{t('checker.addons_fees')}</span>
                <span>{formatCurrency(addOnsPrice)}</span>
            </div>
            <div style={detailRowStyle}>
         
                <span>{t('checker.sales_tax', { rate: Math.round(taxRate * 100) })}</span>
                <span>{formatCurrency(taxAmount)}</span>
            </div>
            
            <h4 style={{ borderBottom: '1px solid #ddd', paddingBottom: '5px', marginTop: '30px', color: '#333' }}>{t('checker.payment_summary_title')}</h4>

            <div style={detailRowStyle}>
           
         <span>{t('checker.payment_status')}</span>
                <span style={{ fontWeight: 'bold', color: getPaymentStatusDisplay(guest).color }}>{getPaymentStatusDisplay(guest).value}</span>
            </div>
             <div style={detailRowStyle}>
                <span>{t('checker.payment_method')}</span>
                <span>{guest.paymentMethod ||
'N/A'}</span>
            </div>
            <div style={detailRowStyle}>
                <span>{t('checker.deposit_required')}</span>
                <span>{formatCurrency(guest.depositRequired)}</span>
            </div>
            
            <div style={summaryStyle}>
     
                <div style={detailRowStyle}>
                    <span style={{ fontSize: '1.3em', color: '#28a745' }}>{t('checker.total_final_price')}</span>
                    <span style={{ fontSize: '1.3em', color: '#dc3545' }}>{formatCurrency(totalDue)}</span>
                </div>
            </div>
    
         
            <p style={{ textAlign: 'center', marginTop: '30px', fontSize: '0.9em', color: '#6c757d' }}>
                {t('checker.footer_note', { hotel: guest.hotelName || 'the Hotel' })}
            </p>
        </div>
    );
};
// --- END FINAL INVOICE COMPONENT ---

const GuestStatusChecker = () => {
    const { t } = useTranslation();
    const [searchInput, setSearchInput] = useState('');
const [guestData, setGuestData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const invoiceRef = useRef(null);
    
// Invoice Download Handler
    const handleDownloadInvoice = () => {
        if (!invoiceRef.current) {
            alert(t('checker.invoice_not_found'));
return;
        }
        const printContent = invoiceRef.current.innerHTML;
        const printWindow = window.open('', '', 'height=600,width=800');
printWindow.document.write(`<html><head><title>${t('checker.invoice_title')}</title>`);        printWindow.document.write('<style>body{font-family: Arial, sans-serif;}</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(printContent);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
printWindow.onload = function() {
            printWindow.print();
        };
    };
const handleSearch = async (e) => {
        e.preventDefault();
        setError('');
        setGuestData(null);
const searchValue = searchInput.trim();
        if (!searchValue) {
            setError(t('checker.search_placeholder_error'));
return;
        }

        setIsLoading(true);
try {
            const guestsRef = collection(db, 'guests');
const registrationsRef = collection(db, 'guestRegistrations');
            
            let querySnapshot = null;
            let foundData = null;
            let targetCollection = 'guests';
let docId = null;

            // --- Multi-Criteria Search Logic ---
            const searches = [
                // Finalized Guests (CHECKED_IN)
                { field: 'accessKey', value: searchValue.toUpperCase(), collection: guestsRef },
                { field: 'bookingKey', value: searchValue.toUpperCase(), collection: guestsRef }, 
          
        { field: 'email', value: searchValue.toLowerCase(), collection: guestsRef },
                { field: 'phone', value: searchValue, collection: guestsRef },
                // Pending Registrations (PENDING)
                { field: 'bookingKey', value: searchValue.toUpperCase(), collection: registrationsRef },
                { field: 'email', value: searchValue.toLowerCase(), collection: 
registrationsRef }, 
                { field: 'phone', value: searchValue, collection: registrationsRef }, 
            ];
for (const search of searches) {
                let q = query(search.collection, where(search.field, '==', search.value));
querySnapshot = await getDocs(q);

                if (!querySnapshot.empty) {
                    docId = querySnapshot.docs[0].id;
foundData = querySnapshot.docs[0].data();
                    targetCollection = search.collection === guestsRef ? 'guests' : 'registrations';
                    break;
}
            }
            
            // --- Fetch Hotel Details ---
            let hotelName = 'N/A';
let hotelAddress = 'N/A';
            if (foundData && foundData.hotelId) {
                const hotelDoc = await getDoc(doc(db, 'hotels', foundData.hotelId));
if (hotelDoc.exists()) {
                    const hData = hotelDoc.data();
hotelName = hData.name;
                    hotelAddress = hData.address;
                }
            }


            // --- Process Result ---
            if (!foundData) {
                setError(t('checker.booking_not_found'));
} else {
                const finalStatus = targetCollection === 'registrations' ?
(foundData.status || 'PENDING') : foundData.status;

                setGuestData({
                    id: docId,
                    ...foundData,
                    hotelName,
                    hotelAddress,
              
            checkInDisplay: getDisplayDate(foundData.checkInDate || foundData.arrivalTime),
                    checkOutDisplay: getDisplayDate(foundData.checkOutDate || foundData.departureTime),
                    finalPriceDisplay: formatCurrency(foundData.adjustedFinalPrice || foundData.totalPrice), 
                    // Key retrieval/access method retrieval
                    
keyDistributionMethod: foundData.keyDistributionMethod || t('checker.digital_key'), 
                    roomAccessMethod: foundData.roomAccessMethod || t('checker.manual_key'), 
                    status: finalStatus,
                });
}
        } catch (err) {
            console.error("Error fetching guest status:", err);
setError(t('checker.search_error_generic'));
} finally {
            setIsLoading(false);
        }
    };
// Helper component to render the status result
    const StatusResult = ({ guest }) => {
        const { t } = useTranslation();
        const baseStyle = { 
            padding: '20px', 
            borderRadius: '8px', 
            marginTop: '20px', 
            border: '1px solid',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)' 
        };
const detailsRowStyle = { 
            display: 'flex', 
            justifyContent: 'space-between', 
            padding: '8px 0', 
            borderBottom: '1px solid #eee' 
        };
const getStatusStyles = (status) => {
            const isPending = status && (status.includes('PENDING') || status === 'NEW_GUEST' || status === 'SUBMITTED' || status === 'CREATED');
if (isPending) {
                 return { 
                     ...baseStyle, 
                     backgroundColor: '#fffbe6', 
                     borderColor: '#ffc107', 
           
            color: '#856404' 
                 };
}
            switch (status) {
                case 'REJECTED':
                    return { 
                        ...baseStyle, 
                      
    backgroundColor: '#f8d7da', 
                        borderColor: '#dc3545', 
                        color: '#721c24' 
                    };
case 'CHECKED_IN':
                    return { 
                        ...baseStyle, 
                        backgroundColor: '#d4edda', 
                        borderColor: '#28a745', 
 
                        color: '#155724' 
                    };
default:
                    return { ...baseStyle, backgroundColor: '#f0f0f0', borderColor: '#ccc', color: '#333' };
}
        };

        const statusStyle = getStatusStyles(guest.status);
        const isCheckedIn = guest.status === 'CHECKED_IN';
const isPending = !isCheckedIn && guest.status !== 'REJECTED';
        
        // Determine the key retrieval instructions based on the staff setting
        const retrievalMethod = guest.keyDistributionMethod ||
'Digital Access Key (in-app)';
        const roomAccessMethod = guest.roomAccessMethod || 'Key Card (Manual Tap)';
// New price/payment display logic
        const paymentDisplay = getPaymentStatusDisplay(guest);
// --- START ROBUST DATA RETRIEVAL & TRANSFORMATION (Updated Logic) ---
        let displayedAddOns = [];
let displayedPreferences = [];

        // 1. Determine Add-ons: Prioritize finalized array. If empty, use transformation fallback.
if (Array.isArray(guest.approvedAddOns) && guest.approvedAddOns.length > 0) {
            displayedAddOns = guest.approvedAddOns;
} else {
            // Fallback: Check raw flags (e.g., guest.breakfast).
// This applies to both PENDING and CHECKED_IN (if array was missed).
            const fallbackAddons = [];
for (const key in ADDON_RATES_MAP) {
                // Check if the original flag is set to true
                if (guest[key] === true) {
                    fallbackAddons.push(ADDON_RATES_MAP[key]);
}
            }
            displayedAddOns = fallbackAddons;
}

        // 2. Determine Preferences: Prioritize finalized array. If empty, use transformation fallback.
if (Array.isArray(guest.preferredItems) && guest.preferredItems.length > 0) {
            displayedPreferences = guest.preferredItems;
} else {
            // Fallback: Check raw preference fields
            const fallbackPreferences = [];
// Bed Type Preference
            if (guest.bedTypePreference && guest.bedTypePreference !== '') {
                fallbackPreferences.push(`${t('checker.bed_type')} ${guest.bedTypePreference}`);
}

            // Floor Preference
            if (guest.floorPreference && guest.floorPreference !== '') {
                const floorMap = t(`checker.floor_map.${guest.floorPreference}`, { defaultValue: guest.floorPreference });
        displayedPreferences.push(`${t('checker.floor_label')} ${floorMap}`);
            }

            // Room Location Preference (quietHighFloor)
            if (guest.quietHighFloor && guest.quietHighFloor !== '') {
                const quietText = t(`checker.quiet_map.${guest.quietHighFloor}`, { defaultValue: guest.quietHighFloor });
                fallbackPreferences.push(`${t('checker.room_location')} ${quietText}`);
            }

            // Boolean Requests
            if (guest.earlyCheckInRequest === true) {
                fallbackPreferences.push(t('checker.early_checkin_req'));
}
            if (guest.lateCheckOutRequest === true) {
                fallbackPreferences.push('checker.late_checkout_req');
}
            if (guest.accessibilityNeeds === true) {
                fallbackPreferences.push('checker.accessibility_req');
}
            
            displayedPreferences = fallbackPreferences;
}
        // --- END ROBUST DATA RETRIEVAL & TRANSFORMATION ---

        const listItemStyle = {
            fontSize: '0.9em',
            padding: '2px 0',
            marginLeft: '20px',
            listStyleType: 'disc'
        };
// Calculate tax details for Pre-paid section
        const totalDays = safeToDate(guest.checkOutDate)?.getTime() && safeToDate(guest.checkInDate)?.getTime() ?
Math.max(1, Math.ceil((safeToDate(guest.checkOutDate).getTime() - safeToDate(guest.checkInDate).getTime()) / 86400000)) : 0;
        const subtotal = (guest.roomPrice || 0) * totalDays;
const taxRate = guest.taxRate >= 0 ? guest.taxRate : 0.10;
        const taxAmount = subtotal * taxRate;
const touristTax = guest.touristTax >= 0 ? guest.touristTax : 0.00; // Recalculate touristTax safely here for display
        const touristTaxTotal = touristTax * totalDays;
return (
            <div style={statusStyle}>
                <h3 style={{ marginTop: 0, borderBottom: `2px solid ${statusStyle.borderColor}`, paddingBottom: '10px' }}>
                 {isCheckedIn ? t('checker.title_confirmed') :
                     guest.status === 'REJECTED' ? t('checker.title_rejected') : 
                     t('checker.title_pending')}
                </h3>
                
                <p><strong>{t('checker.guest_name')}</strong> {guest.fullName}</p>
                <p><strong>{t('checker.booking_key')}</strong> {guest.bookingKey}</p>
               
                {/* FIX: Changed <h3> to <p> to match closing tag and surrounding elements */}
                <p><strong>{t('checker.checkin_date_label')}</strong> {guest.checkInDisplay}</p>
                <p><strong>{t('checker.checkout_date_label')}</strong> {guest.checkOutDisplay}</p>

                {/* --- PRICE / PAYMENT STATUS (Custom Logic) --- */}
                 <div style={{...detailsRowStyle, fontWeight: 'bold', fontSize: '1.1em', backgroundColor: '#e9f7ef'}}>
                    <strong>{paymentDisplay.label}</strong> 
         
            <span style={{ color: paymentDisplay.color }}>{paymentDisplay.value}</span>
                </div>
                {/* --- END PRICE / PAYMENT STATUS --- */}
                
                {/* --- DISPLAY TAX & ADD-ON DETAILS ONLY IF PRE-PAID --- */}
  
                {isCheckedIn && paymentDisplay.isPrePaid && (
                     <div style={{ backgroundColor: '#e6ffe6', padding: '10px', borderRadius: '4px', marginTop: '10px' }}>
                        <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', borderBottom: '1px dashed #28a745' }}>{t('checker.prepaid_details_title')}</p>
            
             <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em', padding: '2px 0' }}>
                            <span>{t('checker.room_tax_subtotal', { rate: Math.round(taxRate * 100) })}</span> 
                            <span>{formatCurrency(taxAmount)}</span>
            
            </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em', padding: '2px 0' }}>
                            <span>{t('checker.tourist_tax', { price: formatCurrency(touristTax) })}</span> 
                            <span>{formatCurrency(touristTaxTotal)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em', padding: '2px 0' }}>
                            <span>{t('checker.addons_fees')}</span> 
                            <span>{formatCurrency(guest.addOnsPrice || 0)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1em', padding: '5px 0 0 0', borderTop: '1px solid #28a745' }}>
                            <strong>{t('checker.total_price_label')}</strong> 
                            <strong>{guest.finalPriceDisplay}</strong>
                        </div>
                    </div>
                )}
                
 
                {/* --- START GUEST SUBMISSIONS SECTION (NEW COMBINED BLOCK) --- */}
                {guest.status !== 'REJECTED' && (
                    <div style={{ 
                        padding: '10px', 
       
                    border: isPending ? '1px dashed #ffc107' : '1px solid #ddd', 
                        marginTop: '15px', 
                        backgroundColor: isPending ? '#fcf8e3' : '#f0f8ff', 
                 
        borderRadius: '5px' 
                    }}>
                        <h4 style={{ margin: '0 0 10px 0', paddingBottom: '5px', borderBottom: '1px dashed #ccc', color: isPending ?
'#856404' : '#007bff' }}>
                            {t('checker.submissions_title')} ({isPending ? t('checker.status_pending_review') : t('checker.status_finalized')})
                        </h4>

                        {/* APPROVED/REQUESTED ADD-ONS SECTION */}
          
                <div style={{ margin: '0 0 10px 0' }}>
                            <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', fontSize: '0.95em' }}>
                                {isPending ? t('checker.requested_addons') : t('checker.approved_addons_included')}
                            </p>
                            {displayedAddOns.length > 0 ?
( 
                                <ul style={{ margin: '5px 0 0 0', padding: 0 }}>
                                    {displayedAddOns.map((item, index) => (
                   
                        <li key={`addon-${index}`} style={listItemStyle}>
                                            {item.name} {item.price ? <span style={{ color: '#dc3545', fontWeight: 'bold' }}>({formatCurrency(item.price)})</span> : ''}
                      
                    </li>
                                    ))}
                                </ul>
              
                ) : (
                                <p style={{ margin: '5px 0 0 20px', fontSize: '0.9em' }}>
                                    {t('checker.no_services', { status: isPending ? t('requested') : t('approved') })}
                </p>
                            )}
                        </div>

              
            {/* PREFERRED ITEMS SECTION */}
                        <div style={{ margin: '10px 0 0 0', paddingTop: '10px', borderTop: '1px dashed #eee' }}>
                            <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', fontSize: '0.95em' }}>{t('checker.preferences_subtitle')}</p>
     
                        {displayedPreferences.length > 0 ?
( 
                                <ul style={{ margin: '5px 0 0 0', padding: 0 }}>
                                    {displayedPreferences.map((item, index) => (
                   
                        <li key={`pref-${index}`} style={listItemStyle}>
                                            {item}
                                 
        </li>
                                    ))}
                                </ul>
                         
    ) : (
                                <p style={{ margin: '5px 0 0 20px', fontSize: '0.9em' }}>{t('checker.no_preferences_added')}</p>
                            )}
                     
    </div>
                    </div>
                )}
                {/* --- END GUEST SUBMISSIONS SECTION --- */}


                {/* --- PENDING Status Display --- */}
                
{isPending && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: `1px dashed ${statusStyle.borderColor}` }}>
                               <p style={{ fontWeight: 'bold' }}>{t('checker.pending_msg')}</p>
        <p style={{ margin: 0 }}>{t('checker.pending_next_step')}</p>
    </div>
                )}

               
  {/* --- REJECTED Status Display --- */}
                {guest.status === 'REJECTED' && (
                    <div style={{ padding: '10px', backgroundColor: '#f5c6cb', borderRadius: '5px', marginTop: '15px' }}>
                        <p style={{ margin: 0, fontWeight: 'bold' }}>{t('checker.rejection_reason_title')}</p>
        <p style={{ margin: '5px 0 0 0' }}>{guest.rejectionReason || t('checker.rejection_default')}</p>
    </div>
                )}
                
      
            {/* --- CHECKED_IN Status Display (UPDATED) --- */}
                {isCheckedIn && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: `1px dashed ${statusStyle.borderColor}` }}>
                        <h4 style={{ margin: '0 0 10px 0', color: statusStyle.color }}>{t('checker.finalized_details_title')}</h4>
                        
                        <div style={detailsRowStyle}>
                            <strong>{t('checker.assigned_room_label')}</strong> 
                     
                <span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{t('checker.room_prefix')} {guest.roomNumber}</span>
                        </div>
                        
                        <div style={detailsRowStyle}>
             
                <strong>{t('checker.digital_key_label')}</strong> 
                            <span style={{ fontWeight: 'bold', fontSize: '1.1em', color: '#007bff' }}>{guest.accessKey}</span>
                        </div>
                      
    
                        {/* Room Access Method */}
                        <div style={{...detailsRowStyle, backgroundColor: '#f0f8ff'}}>
                            <strong>{t('checker.room_access_method_label')}</strong> 
            
                  <span style={{ fontWeight: 'bold', color: '#dc3545' }}>{roomAccessMethod}</span>
                        </div>
                        
                        {/* PAYMENT DETAILS */}
   
                          <div style={detailsRowStyle}>
                            <strong>{t('checker.payment_method_label')}</strong> 
                            <span>{guest.paymentMethod ||
'N/A'}</span>
                        </div>
                        
                        {/* TOTAL FINAL PRICE */}
                        
<div style={{...detailsRowStyle, fontWeight: 'bold', fontSize: '1.1em', backgroundColor: '#e9f7ef'}}>
                            <strong>{t('checker.total_final_price')}</strong> 
                            <span style={{ color: '#dc3545' }}>{guest.finalPriceDisplay}</span>
                        </div>
    
                      
                        {/* Staff Billing Notes */}
                        {guest.billingNotes && (
                          
    <div style={{ backgroundColor: '#f0f8ff', padding: '10px', borderRadius: '4px', marginTop: '10px' }}>
                                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '0.9em' }}>{t('checker.billing_notes_label')}</p>
                                <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>{guest.billingNotes}</p>
     
                        </div>
                        )}
                        
                        {/* Download Final Invoice */}
 
                        <div style={{ padding: '15px 0', borderTop: '1px solid #ddd', marginTop: '10px' }}>
                            <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>{t('checker.invoice_billing_title')}</p>
                          
    <button 
                                onClick={handleDownloadInvoice} 
                                style={{ 
                               
      width: '100%', 
                                    padding: '10px', 
                                    backgroundColor: '#dc3545', 
                 
                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '5px', 
   
                                  cursor: 'pointer' 
                                }}>
                               {t('checker.btn_download_pdf')}
                            </button>
                        </div>
                        
                      
  {/* KIND MESSAGE AND KEY DISTRIBUTION - REVISED FOR CLARITY ON ACCESS KEY */}
                        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                            
                   
              {/* KEY RETRIEVAL METHOD (The WHERE) */}
                            <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#007bff' }}>
                               {t('checker.key_retrieval_method', { method: retrievalMethod })}
            </p>

                            <p style={{ margin: '0 0 10px 0', fontSize: '0.95em', lineHeight: '1.4' }}>
                               {t('checker.welcome_msg', { name: guest.fullName, hotel: guest.hotelName || 'our hotel' })}
            </p>
                            
                            {/* CLARIFICATION ON DIGITAL ACCESS KEY */}
                            <p style={{ margin: '0 0 10px 0', fontSize: '0.95em', lineHeight: 
'1.4', fontWeight: 'bold' }}>
                               {t('checker.system_access_desc')}
            </p>
                            
                            {/* GUEST PAGE LINK INSTRUCTIONS (PROFESSIONAL TONE) */}
                             <p style={{ margin: '0 0 15px 0', fontSize: 
'0.95em', lineHeight: '1.4' }}>
                                {t('checker.guest_page_instr')}
            </p>
                            
                            {/* Conditional Instructions for Room Entry */}
                            {retrievalMethod.toLowerCase().includes('reception') ||
roomAccessMethod.toLowerCase().includes('key card') ? (
                                <p style={{ margin: 0, fontSize: '0.95em', lineHeight: '1.4' }}>
                                    {t('checker.room_entry_reception', { method: roomAccessMethod, number: guest.roomNumber })}
                </p>
                            ) : (
                                <p style={{ margin: 0, 
fontSize: '0.95em', lineHeight: '1.4' }}>
                                    {t('checker.room_entry_digital', { method: roomAccessMethod })}
                </p>
          
                    )}
                            
                            <p style={{ margin: '10px 0 0 0', fontSize: '0.95em', lineHeight: '1.4' }}>
               
                  {t('checker.wishing_stay')}
</p>
                        </div>
                        
                    </div>
                )}
            </div>
    
        );
    };

    return (
        <div className="container" style={{ maxWidth: '600px', margin: '50px auto' }}>
            <h1 style={{ textAlign: 'center', color: '#dc3545' }}>{t('checker.main_title')}</h1>
            <p style={{ textAlign: 'center', color: '#6c757d', marginBottom: '20px' }}>
                {t('checker.main_desc')}
        
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearch} style={{ display: 'flex', marginBottom: '30px' }}>
                <input
                    type="text"
                    value={searchInput}
      
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={t('checker.search_placeholder')}
                    style={{ flexGrow: 1, padding: '12px', border: '1px solid #ccc', borderRadius: '5px 0 0 5px', fontSize: '16px' }}
                    disabled={isLoading}
  
                />
                <button
                    type="submit"
                    disabled={isLoading}
                    style={{ padding: '12px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', 
borderRadius: '0 5px 5px 0', cursor: 'pointer', fontWeight: 'bold' }}
                >
                    {isLoading ? t('checker.searching') : t('checker.btn_check')}
                </button>
            </form>

            {/* Status Messages */}
            {error && (
                <div style={{ padding: '15px', backgroundColor: '#f8d7da', borderColor: '#dc3545', color: '#721c24', border: '1px solid', borderRadius: '5px', marginBottom: '20px', fontWeight: 'bold' }}>
     
                {t('checker.error_prefix')} {error}
                </div>
            )}
            
            {/* Display Guest Data (StatusResult Component) */}
            {guestData && <StatusResult guest={guestData} />}

          
            {/* Hidden component for printing the invoice (referenced by invoiceRef) */}
            {guestData && guestData.status === 'CHECKED_IN' && (
                <div style={{ display: 'none' }}>
                    <div ref={invoiceRef}>
                        <FinalGuestInvoice guest={guestData} />
   
                    </div>
                </div>
            )}

        </div>
    );
};
export default GuestStatusChecker;